import { Link } from "react-router-dom";
import styled from "styled-components";
import { useRecoilValue } from "recoil";
import { useLoginUserInfo } from "../redux/hook/useLoginUserInfo";
import { selectSiteIdState, userRoleState } from "styles/atoms";
import { PageHeaderTitleButtonStyle } from "components/layout/Layout";
import { NoLinePrimaryBtn, OutlinePrimaryBtn, SolidPrimaryBtn, SolidRedBtn } from "components/button/Button";
import { useCallback, useEffect, useState } from "react";
import { aesCBCDecrypt, ellipsisText, formattedDateYYYYMMDD, getCurrentYYMMDDD, NODATATEXT, ROLE_DIRECTOR, ROLE_PARENT, ROLE_TEACHER } from "utils/commonUtils";
import { getAttendanceCount, getClass, getUser, userApproval } from "api/api";
import { AttendanceCountQueryParams, AttendanceCountResponseType, ClassInfo, ClassQueryParams, ListResult, SingleResult, UserInfo } from "type/type";
import { NoDataWithImageText } from "components/NoDataComponent";
import Loading from "components/Loading";
import { FlexWrapC, FlexWrapR } from "components/common/CommonLayout";
import { colors } from "styles/global-styles";
import refreshIcon from "assets/image/common/refresh.svg";

const AttendanceProgressbar = styled.div`
  width: 100%;
  max-width: 120px;
  height: 1.25rem;
  border-radius: 0.5rem;
  background-color: ${colors.lightGray};
  position: relative;
`;
const ProgressingBar = styled.div<{ width: number }>`
  width: ${(props) => props.width ? `${props.width}%` : 0};
  height: 100%;
  background-color: ${({theme}) => theme.newTheme.default.primary};
  border-radius: 0.5rem;

  & ~ span {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    font-size: 0.75rem;
  }
`;

const DashboardContainer = styled.div`
  overflow: hidden;
  height: 100%;
`;

const DashboardArticleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: calc(100% - 4.5rem);
  overflow: auto;
`;

const DashboardArticle = styled.article`
  background-color: ${({theme}) => theme.rootWhite};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  overflow: hidden;
  border-radius: 0.5rem;

  .article-header {
    line-height: 1.25;
  }

  .article-header-title {
    color: ${({theme}) => theme.newTheme.text.mainText};
    font-weight: 600;
    font-size: 1rem;
  }
  
  // 출석현황 표
  .primary {
    overflow: auto;
  }
  // 알림 리스트 wrap
  .list-wrap {
    overflow: auto;
    padding-right: 0.5rem;
  }
  // 등록요청 wrap
  .join-wrap {
    height: calc(100% - 1.25rem);
    overflow: auto;
  }
`;

// 출석현황 표 CSS
const StickyTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 0;

  th,
  td {
    padding: 0.75rem;
    margin: 0;
    border-bottom: 1px solid ${({theme}) => theme.newTheme.border.primaryBd};
    background-clip: padding-box;
    scroll-snap-align: start;
    text-align: center;
  }
  thead {
    position: relative;
    z-index: 20;

    th {
      position: sticky;
      top: 0;
      background-clip: padding-box;
      color: ${({theme}) => theme.newTheme.table.tableHeadText};
      background-color: ${({theme}) => theme.newTheme.table.tableHeadBg};
      border: 0;
      &.pin {
        left: 0;
        z-index: 22;
      }
      &:first-of-type {
        border-top-left-radius: 0.5rem;
        border-bottom-left-radius: 0.5rem;
      }
      &:last-of-type {
        border-top-right-radius: 0.5rem;
        border-bottom-right-radius: 0.5rem;
      }
    }
  }
  tbody {
    z-index: 15;
    position: relative;

    tr:hover {
      background-color: ${({ theme }) => theme.newTheme.table.tableBodyHoverBg};
    }
  }
`;

// 알림 CSS
const AlarmListOne = styled.ul`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.newTheme.border.primaryBd};
  
  a {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;

    img {
      width: 1.5rem;
      height: 1.5rem;
    }
    ~ div {
      width: 1.5rem;
      height: 1.5rem;
      opacity: 0;

      img {
        width: 1rem;
        height: 1rem;
      }
    }
  }
  li {
    display: inline-flex;
    flex-direction: column;
    gap: 0.25rem;
    &:first-of-type {
      width: 3rem;
      align-items: center;
    }
  }
  span {
    font-size: 0.875rem;
    color: ${({theme}) => theme.newTheme.text.btnText_disabled};
  }
  .alarm-title {
    font-size: 1rem;
    color: ${({theme}) => theme.newTheme.text.mainText};
  }
  &.read * {
    color: ${({theme}) => theme.newTheme.text.btnText_disabled};
  }
  &:hover a ~ div {
    opacity: 0.5;
    cursor: pointer;
  }
`;
// 등록요청 CSS
const JoinNavWrap = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;

  // 탭 button
  button {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.25rem;
    font-size: 1rem;
    padding: 0.25rem 0.5rem;
    border-bottom: 2px solid transparent;
  }
  .join-count {
    padding: 0.25rem 0.5rem;
    border-radius: 0.5rem;
    color: ${({theme}) => theme.newTheme.default.primary};
    background-color: ${({theme}) => theme.newTheme.default.secondary};
  }
  .active {
    color: ${({theme}) => theme.newTheme.text.mainText};
    border-bottom: 2px solid ${({ theme }) => theme.newTheme.text.mainText};
    
    .join-count {
      background-color: ${({theme}) => theme.newTheme.default.primary};
      color: ${({theme}) => theme.newTheme.text.btnText_w};
    }
  }
`;
// 등록요청 하나 wrap
const JoinOneWrap = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  min-height: 10rem;
  padding: 0 1rem 0.5rem 0;
  overflow-x: auto;
  overflow-y: hidden;
`;
// 등록요청 하나
const JoinOne = styled.div`
  width: 10rem;
  height: 10rem;
  padding: 1rem 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid ${({theme}) => theme.newTheme.border.primaryBd};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  > div {
    p:not(.join-class-title) {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.25rem;
    }
  }
  
  .join-class-title {
    color: ${({theme}) => theme.newTheme.default.accent};
  }
  
  .join-student {
    font-size: 1.125rem;
    font-family: var(--font-nanumSquare-acB);
    word-break: break-all;
  }
  
  .join-parent {
    font-size: 1rem;
    font-weight: 600;
  }
  
  .ellipsis-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  &:hover {
    border: 1px solid ${({theme}) => theme.newTheme.default.accent};
  }
`;

// 등록요청
const JoinContent = () => {

  // 전역
  const selectSiteId = useRecoilValue(selectSiteIdState);

  const [loading, setLoading] = useState(true);
  const [activeMenu, setActiveMenu] = useState("parent");

  const [joinTeacherList, setJoinTeacherList] = useState<UserInfo[]>([]);
  const [joinTeacherListCount, setJoinTeacherListCount] = useState(0);

  const [joinParentList, setJoinParentList] = useState<UserInfo[]>([]);
  const [joinParentListCount, setJoinParentListCount] = useState(0);
    
  // role에 따라 API 요청하기
  const handleJoinType = (type: string) => {
    setActiveMenu(type);
  };
  
  // 등록요청 새로고침 버튼
  const resetFilter = () => {
    teacherMainApi();
    parentMainApi();
  }
  // [선생님] 메뉴 타입 선택 시 승인 대기중인 목록 가져오기
  const teacherMainApi = useCallback(async () => {
    try {
      
      setLoading(true);

      const result = await getUser({ site_id: selectSiteId, role: ROLE_TEACHER, approval: 0 });
      
      if (result.code === 200) {
        setJoinTeacherList(result.data);
        setJoinTeacherListCount(result.data.length);
        
      }
      else {
        alert(result.message);
        setJoinTeacherList([]);
        setJoinTeacherListCount(0);
        return;
      }

      setLoading(false);
    } catch (error) {
      console.error("error ", error);
      alert("데이터를 가져오는 중 오류가 발생했습니다.");
    }
  }, [selectSiteId])

  // [학부모] 메뉴 타입 선택 시 학부모 목록 가져오기
  const parentMainApi = useCallback(async () => {
    try {
      
      setLoading(true);

      const result = await getUser({ site_id: selectSiteId, role: ROLE_PARENT, approval: 0 });
      
      if (result.code === 200) {
        setJoinParentList(result.data);
        setJoinParentListCount(result.data.length);
      }
      else {
        alert(result.message);
        setJoinParentList([]);
        setJoinParentListCount(0);
        return;
      }

      setLoading(false);
    } catch (error) {
      console.error("error ", error);
      alert("데이터를 가져오는 중 오류가 발생했습니다.");
    }
  }, [selectSiteId]);


  // 가입 승인/거절
  async function handlerApproval(id: number, approval: number, type: string) {
    const result:ListResult<any> = await userApproval(id, approval);
    if (result.code === 200) {
      if (type === 'teacher') teacherMainApi();
      else parentMainApi();
    }
    else {
      alert(result.message);
      return;
    }
  }

  // 선생님 승인 대기 목록
  const WaitApprovalTeacherListUi = () => {
    return (
      <>
        {!joinTeacherListCount ?
          <NoDataWithImageText text={NODATATEXT.noSign} />
          : ( joinTeacherList.map((teacher) => (
            <JoinOne key={teacher.id}>
              <FlexWrapC>
                <p className="join-class-title">
                    {teacher.temp_class_name || '반 미정'}
                </p>
                <p className="join-student">
                    <span>{aesCBCDecrypt(teacher.name)}</span>
                    <span>선생님</span>
                </p>
                {/* 연락처 - 마스킹 기준 필요 */}
                {/* <p style={{fontSize: '0.75rem'}}> */}
                    {/* 010-1111-2222 */}
                    {/* {teacher.phone} */}
                {/* </p> */}
              </FlexWrapC>
              <FlexWrapR>
                <p>
                    <span className="ellipsis-text">{teacher.login_id}</span>
                </p>
              </FlexWrapR>
              <FlexWrapR $justifyContent="space-between">
                <SolidPrimaryBtn type="button" onClick={() => handlerApproval(teacher.id, 1, 'teacher')}>
                  승인
                </SolidPrimaryBtn>
                <SolidRedBtn type="button" onClick={() => handlerApproval(teacher.id, 2, 'teacher')}>
                  거절
                </SolidRedBtn>
              </FlexWrapR>
            </JoinOne>
          )))
        }
      </>
    )
  }
  // 학부모 승인 대기 목록
  const WaitApprovalParentListUi = () => {

    return (
      <>
        {!joinParentListCount ?
          <NoDataWithImageText text={NODATATEXT.noSign} />
          : ( joinParentList.map((parent, index) => (
            <JoinOne key={parent.id}>
              {parent.temp_child_student ?
                <FlexWrapC>
                  <p className="join-class-title">
                    <span>{parent.temp_child_student.class_name}</span>
                  </p>
                  <p>
                    <span className="join-student ellipsis-text">{aesCBCDecrypt(parent.temp_child_student.name)}</span>
                  </p>
                </FlexWrapC>
                : <FlexWrapC><p className="join-class-title">등록된 자녀정보가 없습니다.</p></FlexWrapC>
              }
              {/* 학부모 정보 */}
              <FlexWrapR>
                <p>
                  <span className="join-parent">{aesCBCDecrypt(parent.name)}</span>
                  <span> 학부모님</span>
                </p>
              </FlexWrapR>
              <FlexWrapR $justifyContent="space-between">
                <SolidPrimaryBtn type="button" className="default-btn" onClick={() => handlerApproval(parent.id, 1, 'parent')}>
                  승인
                </SolidPrimaryBtn>
                <SolidRedBtn type="button" className="default-btn" onClick={() => handlerApproval(parent.id, 2, 'parent')}>
                  거절
                </SolidRedBtn>
              </FlexWrapR>
            </JoinOne>
          )))
        }
      </>
    )
  }

  useEffect(() => {
    if (activeMenu === "teacher") teacherMainApi();
    else parentMainApi();
  }, [teacherMainApi, parentMainApi, activeMenu]);

  useEffect(() => {
    teacherMainApi();
    parentMainApi();
  }, []);

  return (
    <DashboardArticle style={{height: '42%', minHeight: '220px'}}>
      <FlexWrapR className="article-header" $alignItems="center" $justifyContent="space-between" $gap="1rem">
        <FlexWrapR>
          <span className="article-header-title">등록요청</span>
        </FlexWrapR>
        <OutlinePrimaryBtn type="button" className="contain-icon" style={{padding: '0.25rem 0.8rem', marginRight: 'auto'}} onClick={resetFilter}>
          <img srcSet={refreshIcon} alt="refresh"/>
        </OutlinePrimaryBtn>
      </FlexWrapR>
      <FlexWrapC className="join-wrap" $gap="1rem">
        {loading ? <Loading /> : (
          <>
            <JoinNavWrap>
              <button
                type="button"
                className={`text-btn ${activeMenu === "parent" ? "active" : ""}`}
                onClick={() => handleJoinType("parent")}
              >
                <p>학부모 요청</p>
                <p className="join-count">{joinParentListCount}</p>
              </button>
              <button
                type="button"
                className={`text-btn ${activeMenu === "teacher" ? "active" : ""}`}
                onClick={() => handleJoinType("teacher")}
              >
                <p>선생님 요청</p>
                <p className="join-count">{joinTeacherListCount}</p>
              </button>
            </JoinNavWrap>
            <JoinOneWrap>
              {activeMenu === "teacher" ? <WaitApprovalTeacherListUi /> : <WaitApprovalParentListUi />}
            </JoinOneWrap>
          </>
        )}
      </FlexWrapC>
    </DashboardArticle>
  );
};

const sampleData2: any[] = [];

// 신규알림
function AlarmContent() {
  
  const defaultObj = {
    index: 0,
    type: "sign",
    read: false,
    context: "",
    time: "",
  };

  const [alertObj, setAlertObj] = useState<any[]>([defaultObj]);
  const [notReadAlertObj, setNotReadAlertObj] = useState<any[]>([]);

  // 개별 읽음여부 - read 상태 변경 API
  function handleIsRead(idx: number) {
    setAlertObj((prev) =>
      prev.map((obj) => (obj.index === idx ? { ...obj, read: true } : obj))
    );
    setNotReadAlertObj(notReadAlertObj.filter(o => o.index !== idx));
  }

  // 전체 읽음 - read 상태 변경 API
  const handleReadAlarm = async () => {
    setAlertObj((prev) =>
      prev.map((obj) => ({ ...obj, read: true }))
    );

    setNotReadAlertObj([]);
  };
  
  function handleDeleteAlert(idx: number) {
    setAlertObj((prev) => prev.filter((obj) => obj.index !== idx));
    setNotReadAlertObj(notReadAlertObj.filter(o => o.index !== idx));
  }
  
  useEffect(() => {
    setAlertObj(sampleData2);
    setNotReadAlertObj(sampleData2.filter(d => !d.read));
  }, []);

  return (
    <DashboardArticle style={{width: '40%'}}>
      <FlexWrapR className="article-header" $alignItems="center" $justifyContent="space-between">
        <FlexWrapR>
          <span className="article-header-title">신규알림</span>
          <span>최근 7일간: 총 {alertObj.length}건 / 미열람: { notReadAlertObj.length }건</span>
        </FlexWrapR>
        <p>
          <NoLinePrimaryBtn type="button" className="little-btn" onClick={handleReadAlarm}>
            전체읽음
          </NoLinePrimaryBtn>
        </p>
      </FlexWrapR>
      <FlexWrapC className="list-wrap">
          {alertObj.length ? alertObj.map((d) => (
          <AlarmListOne key={d.index} className={`${d.read ? "read" : ""}`}>
            <Link to={""} onClick={() => handleIsRead(d.index)}>
              {/* 알림 타입에 따라 아이콘과 text 바뀌어야함 */}
              <li>
                <img src={d.read ? "/image/icon/bell_g.svg" : "/image/icon/bell_c.svg"} alt="bell" />
                <span>{d.type === 'sign' ? "가입" : d.type === 'note' ? "알림장" : ""}</span>
              </li>
              <li>
                <span className="alarm-title">{d.context}</span>
                <span>{d.time}</span>
              </li>
            </Link>
            <FlexWrapR $gap="0" $alignItems="center" $justifyContent="center" onClick={() => handleDeleteAlert(d.index)}>
              <img src="/image/icon/close.svg" alt="close" />
            </FlexWrapR>
          </AlarmListOne>
          ))
          : <p style={{textAlign: 'center'}}>신규 알림이 없습니다.</p>
        }
      </FlexWrapC>
    </DashboardArticle>
  );
};

// [대시보드] 출석현황
function TodayAttendanceBoard() {
    
  // 전역
  const user_role = useRecoilValue(userRoleState);
  const { access } = useLoginUserInfo();
  const { user_id } = access;

  const todayText = getCurrentYYMMDDD();
  const today = formattedDateYYYYMMDD(new Date());

  const [loading, setLoading] = useState<boolean>(true);

  // 반 정보
  const [classList, setClassList] = useState<ClassInfo[]>([]);

  // 반 출결
  const [countData, setCountData] = useState<AttendanceCountResponseType[]>([]);

  // 출석현황 새로고침 버튼
  const resetFilter = () => {
    mainApi();
  }

  // 반 출결 전체 불러오기
  const mainApi = useCallback(async () => {
    try {

      // 담당반 클래스 아이디 리스트 업데이트
      const classQueryParams: ClassQueryParams = {
        name: "",
        user_id: user_role === ROLE_DIRECTOR ? 0 : user_id,
        response_format: "",
        page: 1,
        size: 99999999
      };

      //클래스 조회
      const classResult: ListResult<ClassInfo> = await getClass(classQueryParams);

      if (classResult.code === 200) {

        const userClassList: ClassInfo[] = classResult.data;
        setClassList(userClassList);
        
        if (userClassList.length === 0) return;
  
        // 관리자의 경우
        if (user_role === ROLE_DIRECTOR) {
  
          // 출결 통계 카운트 조회
          const promises = userClassList.map((c: ClassInfo) => {
            const queryParams:AttendanceCountQueryParams = {
              class_id: c.id,
              date: today,
            }
            return getAttendanceCount(queryParams);
          });
  
          const results = await Promise.allSettled(promises);
          const data = results.map(res => {
            if (res.status === 'fulfilled' && res.value)
              return res.value.data;
            return [];
          })
  
          setCountData(data);
  
          // 성공과 실패 결과 처리
          const successCnt = results.filter(res => res.status === 'fulfilled' && res.value.code === 200).length;
          const erroRes = results.filter(res => res.status === 'rejected' || (res.status === 'fulfilled' && res.value.code !== 200));
  
          // 결과에 따른 알림 표시
          if (successCnt === 0) {
            alert('반 별 출결 현황 조회가 실패됐습니다.');
            console.error(`Error in Promise `, erroRes);
          }
          else if (userClassList.length > successCnt) {
            alert('일부만 조회가 완료되었습니다.');
            console.error(`부분 Error in Promise `, erroRes);
          }

        }
        else { // 담당선생님인 경우
          
          if (userClassList.length !== 0 && userClassList[0].id === 0) {
            return;
          }
          const userClassId = userClassList[0].id;
  
          const queryParams:AttendanceCountQueryParams = {
            class_id: userClassId,
            date: today,
          }
          
          //출결 통계 카운트 조회
          const res:SingleResult<AttendanceCountResponseType> = await getAttendanceCount(queryParams);
          if (res.code === 200) {
  
            const result:AttendanceCountResponseType = res.data;
            setCountData([result]);
          }
        }
      }
      
    }
    catch (error) {
      console.error("api error ", error);
    }
    finally {
      setLoading(false);
    }
  }, [user_role, today, user_id]);

  useEffect(() => {
    mainApi();
  }, [mainApi]);

  return (
    <DashboardArticle style={{width: '58%', flexGrow: '1'}}>
      <FlexWrapR className="article-header" $alignItems="center" $justifyContent="space-between" $gap="1rem">
        <FlexWrapR $alignItems="flex-end">
          <span className="article-header-title">출석현황</span>
          <span>총 {classList.length}반</span>
        </FlexWrapR>
        <OutlinePrimaryBtn type="button" className="contain-icon" style={{padding: '0.25rem 0.8rem', marginRight: 'auto'}} onClick={resetFilter}>
          <img srcSet={refreshIcon} alt="refresh"/>
        </OutlinePrimaryBtn>
        <p>
          <span>{todayText}</span>
        </p>
      </FlexWrapR>
      <div className="primary" aria-label="data table" tabIndex={0}>
        <StickyTable>
          <thead>
            <tr>
              <th className="w-10">번호</th>
              <th>반이름</th>
              <th>담임</th>
              <th className="w-10">전체인원</th>
              <th className="w-10">출석률</th>
              <th className="w-8">등교</th>
              <th className="w-8">하교</th>
              <th className="w-8">결석</th>
              <th className="w-8">조퇴</th>
              <th className="w-8">인정결석</th>
            </tr>
          </thead>
          <tbody>
            {loading ?
              <tr>
                <td colSpan={9} style={{borderBottom: 'none'}}><Loading /></td>
              </tr>
            :
            (!countData.length ? 
              <tr>
                <td colSpan={8}>
                  <div><p>조회된 데이터가 없습니다.</p></div>
                </td>
              </tr>
            :
            countData.map((c, index) => (
              <tr key={index + 1}>
                <td className="w-10">{index + 1}</td>
                <td>{ ellipsisText(classList[index].name || '-', 15) }</td>
                <td>{ ellipsisText(aesCBCDecrypt(classList[index].user_name) || '-', 10)}</td>
                <td className="w-10">{c.student_total_count}명</td>
                <td>
                  <AttendanceProgressbar>
                    <ProgressingBar className="progressing" width={(c.attendance_count / c.student_total_count) * 100} />
                    <span>{c.attendance_count ? `${c.attendance_count}/${c.student_total_count}(${((c.attendance_count / c.student_total_count) * 100).toFixed(1)}%)` : "0%"}</span>
                  </AttendanceProgressbar>
                </td>
                <td className="w-8">{ c.check_in_count}명</td>
                <td className="w-8">{ c.check_out_count}명</td>
                <td className="w-8 text-red">{ c.absence_count}명</td>
                <td className="w-8">{ c.early_leave_count}명</td>
                <td className="w-8 text-yellow">{ c.approved_absence_count}명</td>
              </tr>
            ))
            )}
          </tbody>
        </StickyTable>
      </div>
    </DashboardArticle>
  )
}


function Main() {
  // Recoil의 값을 가져오려면 useRecoilValue로 가져오면 됨
  const user_role = useRecoilValue(userRoleState);

  return (
    <DashboardContainer>
      <PageHeaderTitleButtonStyle>
        <p className="header-title">대시보드</p>
      </PageHeaderTitleButtonStyle>
      <DashboardArticleWrap>
        <FlexWrapR style={{height: `${user_role !== ROLE_DIRECTOR ? '100%' : '55%'}`, minHeight: '220px'}}>
          <TodayAttendanceBoard />
          {/* <AlarmContent /> */}
        </FlexWrapR>
        {user_role === ROLE_DIRECTOR && <JoinContent />}
      </DashboardArticleWrap>
    </DashboardContainer>
  );
}

export default Main;